import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { geoEquirectangular, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import worldTopology from 'world-atlas/countries-110m.json';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw, Globe,
  Thermometer, Mountain, MapPin, Waves, Cloud, Wind, Info, Zap
} from 'lucide-react';

// ── Real-world cartographic data (Natural Earth 110m via world-atlas) ──
const MAP_W = 1000;
const MAP_H = 500;
const COUNTRY_FEATURES = feature(worldTopology, worldTopology.objects.countries).features;
const MAP_PROJECTION = geoEquirectangular()
  .scale(MAP_H / Math.PI)
  .translate([MAP_W / 2, MAP_H / 2]);
const MAP_PATH = geoPath(MAP_PROJECTION);
const COUNTRY_PATHS = COUNTRY_FEATURES.map((f, i) => ({
  id: f.id || i,
  name: (f.properties && f.properties.name) || '',
  d: MAP_PATH(f),
})).filter(c => c.d);


// ── Data ──────────────────────────────────────────────────────────────
const TECTONIC_PLATES = [
  { id: 'pacific', name: 'Pacific Plate', x: 15, y: 45, w: 22, h: 30, color: '#3b82f6', info: 'Largest tectonic plate. Under the entire Pacific Ocean. Subducting under many surrounding plates, creating the Ring of Fire.' },
  { id: 'north-american', name: 'North American', x: 28, y: 22, w: 18, h: 22, color: '#22c55e', info: 'Carries North America, Greenland, and part of the Atlantic. Moving westward ~2.5 cm/year.' },
  { id: 'south-american', name: 'South American', x: 32, y: 52, w: 14, h: 22, color: '#f59e0b', info: 'Moving westward, pushing the Nazca plate under it. The Andes mountains are formed by this collision.' },
  { id: 'eurasian', name: 'Eurasian Plate', x: 55, y: 20, w: 25, h: 20, color: '#a78bfa', info: 'Covers Europe and most of Asia. Collision with Indian plate formed the Himalayas — still growing ~5mm/year!' },
  { id: 'african', name: 'African Plate', x: 50, y: 42, w: 15, h: 25, color: '#ef4444', info: 'The Great Rift Valley is splitting this plate! In millions of years, East Africa will become a separate continent.' },
  { id: 'indo-australian', name: 'Indo-Australian', x: 68, y: 48, w: 18, h: 20, color: '#ec4899', info: 'Carries India and Australia. India crashes into Eurasia at ~5cm/year, pushing the Himalayas higher!' },
  { id: 'antarctic', name: 'Antarctic Plate', x: 45, y: 82, w: 30, h: 12, color: '#e2e8f0', info: 'Surrounded by divergent boundaries. Antarctica was once near the equator as part of Gondwana!' },
];

const COUNTRIES = [
  { id: 'india', name: 'India', capital: 'New Delhi', x: 67, y: 42, color: '#f97316', pop: '1.4B', fact: 'World\'s most populous country. Home to the Himalayas, Thar Desert, and the Ganges Delta.' },
  { id: 'brazil', name: 'Brazil', capital: 'Brasilia', x: 35, y: 58, color: '#22c55e', pop: '214M', fact: 'Contains 60% of the Amazon Rainforest — the "lungs of the Earth" producing 20% of world\'s oxygen.' },
  { id: 'australia', name: 'Australia', capital: 'Canberra', x: 82, y: 68, color: '#f59e0b', pop: '26M', fact: 'Only country that is also a continent. Home to unique marsupials like kangaroos and koalas.' },
  { id: 'japan', name: 'Japan', capital: 'Tokyo', x: 83, y: 32, color: '#ef4444', pop: '125M', fact: 'An archipelago of 6,852 islands sitting on the Ring of Fire. Experiences ~1,500 earthquakes per year!' },
  { id: 'egypt', name: 'Egypt', capital: 'Cairo', x: 54, y: 38, color: '#fbbf24', pop: '104M', fact: 'Home to the Great Pyramids of Giza, one of the Seven Wonders. The Nile is the longest river in Africa.' },
  { id: 'usa', name: 'United States', capital: 'Washington D.C.', x: 22, y: 32, color: '#3b82f6', pop: '331M', fact: 'Third-largest country by area. Has diverse geography from Alaska\'s glaciers to Hawaii\'s volcanoes.' },
  { id: 'russia', name: 'Russia', capital: 'Moscow', x: 70, y: 18, color: '#a78bfa', pop: '144M', fact: 'Largest country by area (17.1M km²). Spans 11 time zones from Europe to the Pacific!' },
  { id: 'china', name: 'China', capital: 'Beijing', x: 76, y: 33, color: '#ec4899', pop: '1.4B', fact: 'Home to the Great Wall (21,196 km). Has the world\'s highest plateau (Tibetan Plateau, 4,500m avg).' },
];

const CLIMATE_ZONES = [
  { id: 'tropical', name: 'Tropical', color: '#22c55e', temp: '25-35°C', regions: ['Amazon', 'Congo Basin', 'Southeast Asia'], desc: 'Hot and wet year-round. Dense rainforests, incredible biodiversity. Home to 50% of all species!' },
  { id: 'arid', name: 'Arid/Desert', color: '#f59e0b', temp: '0-50°C', regions: ['Sahara', 'Arabian', 'Gobi'], desc: 'Less than 250mm rainfall/year. Extreme temperature swings. Sahara is as big as the entire USA!' },
  { id: 'temperate', name: 'Temperate', color: '#3b82f6', temp: '5-25°C', regions: ['Europe', 'Eastern USA', 'Japan'], desc: 'Four distinct seasons. Moderate rainfall. Most of world\'s agricultural land is here.' },
  { id: 'continental', name: 'Continental', color: '#a78bfa', temp: '-30 to 30°C', regions: ['Siberia', 'Central Canada', 'Mongolia'], desc: 'Extreme seasonal variation. Very cold winters, warm summers. Permafrost in northern areas.' },
  { id: 'polar', name: 'Polar', color: '#e2e8f0', temp: '-60 to 0°C', regions: ['Antarctica', 'Arctic', 'Greenland'], desc: 'Permanent ice caps. Antarctica holds 70% of Earth\'s fresh water as ice. Polar bears in Arctic only!' },
];

const DISASTERS = [
  { id: 'earthquake', name: 'Earthquake', icon: Zap, x: 83, y: 32, color: '#ef4444', magnitude: '9.0', location: 'Ring of Fire (Japan)', desc: 'Caused by tectonic plate movement. The 2011 Tohoku earthquake caused a tsunami that reached 40m high.' },
  { id: 'volcano', name: 'Volcanic Eruption', icon: Mountain, x: 20, y: 58, color: '#f97316', magnitude: 'VEI 6', location: 'Andes (South America)', desc: 'Magma from mantle reaches surface. The Ring of Fire has 75% of world\'s active volcanoes.' },
  { id: 'tsunami', name: 'Tsunami', icon: Waves, x: 70, y: 50, color: '#3b82f6', magnitude: '30m waves', location: 'Indian Ocean', desc: 'Underwater earthquakes displace massive water. Waves travel at 800 km/h in deep water but slow and grow tall near shore.' },
  { id: 'hurricane', name: 'Hurricane/Cyclone', icon: Wind, x: 25, y: 38, color: '#a78bfa', magnitude: 'Cat 5', location: 'Atlantic/Pacific', desc: 'Massive rotating storms fueled by warm ocean water. Eye of the storm is eerily calm. Can span 1000+ km.' },
  { id: 'drought', name: 'Drought', icon: Thermometer, x: 52, y: 45, color: '#fbbf24', magnitude: 'Severe', location: 'Sub-Saharan Africa', desc: 'Extended period of low rainfall. Climate change is making droughts more frequent and severe globally.' },
];

const MISSIONS = [
  { id: 1, title: 'Tectonic Plates Explorer', difficulty: 1, type: 'tectonics', description: 'Click on tectonic plates to learn about Earth\'s moving puzzle pieces.', goal: 'Discover all 7 major plates', targetCount: 7 },
  { id: 2, title: 'Plate Boundaries', difficulty: 2, type: 'boundaries', description: 'Identify what happens at plate boundaries — convergent, divergent, and transform.', goal: 'Classify 6 boundaries correctly', targetCount: 6 },
  { id: 3, title: 'Climate Zones Map', difficulty: 1, type: 'climate', description: 'Explore the 5 major climate zones of Earth.', goal: 'Explore all climate zones', targetCount: 5 },
  { id: 4, title: 'World Capitals Quest', difficulty: 1, type: 'countries', description: 'Click on countries to discover their capitals, populations, and fun facts.', goal: 'Discover all 8 countries', targetCount: 8 },
  { id: 5, title: 'Natural Disasters', difficulty: 2, type: 'disasters', description: 'Learn about earthquakes, tsunamis, volcanoes, and more!', goal: 'Explore all disaster types', targetCount: 5 },
  { id: 6, title: 'Earth Layers Journey', difficulty: 2, type: 'layers', description: 'Travel from the surface to the core! Explore each layer of Earth.', goal: 'Visit all 5 layers', targetCount: 5 },
  { id: 7, title: 'Climate Change Simulator', difficulty: 3, type: 'climate-change', description: 'Adjust CO₂ levels and see how temperature and sea level change over time!', goal: 'Observe effects at 400, 600, and 800 ppm', targetCount: 3 },
  { id: 8, title: 'Mountain Challenge', difficulty: 2, type: 'mountains', description: 'Discover the world\'s tallest mountains and the forces that created them.', goal: 'Identify all 5 peaks', targetCount: 5 },
  { id: 9, title: 'Ocean Currents', difficulty: 2, type: 'oceans', description: 'Trace the major ocean currents that control global climate.', goal: 'Discover all major currents', targetCount: 5 },
  { id: 10, title: 'Pangaea Time Machine', difficulty: 3, type: 'pangaea', description: 'Travel back 250 million years and watch the supercontinent break apart!', goal: 'Watch the full continental drift', targetCount: 1 },
];

const EARTH_LAYERS = [
  { id: 'crust', name: 'Crust', depth: '0-70 km', temp: '~20°C surface', color: '#92400e', width: '12%', desc: 'Thin rocky shell we live on. Oceanic crust (5-10 km) is thinner than continental crust (30-70 km). Made of silicates.' },
  { id: 'upper-mantle', name: 'Upper Mantle', depth: '70-410 km', temp: '200-900°C', color: '#dc2626', width: '18%', desc: 'Contains the asthenosphere — a partially molten layer on which tectonic plates "float" and move.' },
  { id: 'lower-mantle', name: 'Lower Mantle', depth: '660-2900 km', temp: '900-3700°C', color: '#ef4444', width: '30%', desc: 'Extremely dense, solid rock under immense pressure. Convection currents here drive plate tectonics!' },
  { id: 'outer-core', name: 'Outer Core', depth: '2900-5150 km', temp: '4000-5000°C', color: '#f97316', width: '22%', desc: 'Liquid iron and nickel! Its flow generates Earth\'s magnetic field that protects us from solar radiation.' },
  { id: 'inner-core', name: 'Inner Core', depth: '5150-6371 km', temp: '~5400°C', color: '#fbbf24', width: '18%', desc: 'Solid iron ball as hot as the Sun\'s surface! Despite extreme heat, immense pressure keeps it solid.' },
];

const MOUNTAINS = [
  { id: 'everest', name: 'Mt. Everest', height: '8,849 m', x: 70, y: 35, color: '#e2e8f0', range: 'Himalayas', fact: 'Tallest above sea level. Grows ~4mm/year as India pushes into Eurasia.' },
  { id: 'k2', name: 'K2', height: '8,611 m', x: 68, y: 33, color: '#d1d5db', range: 'Karakoram', fact: 'Second tallest and deadliest — 1 death for every 4 summits. Known as the "Savage Mountain".' },
  { id: 'kilimanjaro', name: 'Mt. Kilimanjaro', height: '5,895 m', x: 55, y: 53, color: '#a8a29e', range: 'Tanzania', fact: 'Africa\'s highest peak. A dormant volcano with glaciers — but they\'re disappearing due to climate change.' },
  { id: 'denali', name: 'Denali', height: '6,190 m', x: 12, y: 16, color: '#cbd5e1', range: 'Alaska Range', fact: 'Tallest in North America. Has the largest vertical rise of any mountain on land (5,500m base to summit).' },
  { id: 'andes-peak', name: 'Aconcagua', height: '6,961 m', x: 30, y: 63, color: '#94a3b8', range: 'Andes', fact: 'Tallest in the Americas and Southern/Western hemispheres. Formed by Nazca plate subduction.' },
];

const OCEAN_CURRENTS = [
  { id: 'gulf-stream', name: 'Gulf Stream', from: { x: 25, y: 40 }, to: { x: 42, y: 28 }, color: '#ef4444', temp: 'Warm', desc: 'Carries warm water from Gulf of Mexico to Europe. Without it, London would be as cold as Labrador!' },
  { id: 'kuroshio', name: 'Kuroshio Current', from: { x: 78, y: 40 }, to: { x: 85, y: 28 }, color: '#f97316', temp: 'Warm', desc: 'Japan\'s "Black Current" — warms the Japanese coast. Equivalent of the Gulf Stream in the Pacific.' },
  { id: 'antarctic-circ', name: 'Antarctic Circumpolar', from: { x: 20, y: 78 }, to: { x: 80, y: 78 }, color: '#3b82f6', temp: 'Cold', desc: 'Strongest current on Earth! Circles Antarctica uninterrupted. Isolates the continent, keeping it frozen.' },
  { id: 'humboldt', name: 'Humboldt Current', from: { x: 28, y: 70 }, to: { x: 25, y: 48 }, color: '#22c55e', temp: 'Cold', desc: 'Brings cold, nutrient-rich water up South America\'s coast. Creates rich fishing grounds. Causes coastal deserts!' },
  { id: 'north-atlantic', name: 'North Atlantic Drift', from: { x: 42, y: 28 }, to: { x: 50, y: 18 }, color: '#ec4899', temp: 'Warm', desc: 'Extension of the Gulf Stream. Keeps Northern Europe much warmer than similar latitudes elsewhere.' },
];

// ── World Map SVG ──────────────────────────────────────────────────
function WorldMap({ items, onItemClick, activeItem, type }) {
  // Ocean currents have from/to only - compute midpoint x/y for markers.
  const positioned = type === 'oceans'
    ? items.map(it => ({ ...it, x: (it.from.x + it.to.x) / 2, y: (it.from.y + it.to.y) / 2 }))
    : items;
  return (
    <div className="relative w-full aspect-[2/1] rounded-2xl overflow-hidden border border-border bg-muted dark:bg-[#0a1628]" data-testid="world-map">
      {/* Ocean gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0c1e3a] to-[#0a1628]" />
      {/* Grid */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        {[...Array(18)].map((_, i) => <line key={`h${i}`} x1="0" y1={`${i * 5.55}%`} x2="100%" y2={`${i * 5.55}%`} stroke="#3b82f6" strokeWidth="0.5" />)}
        {[...Array(36)].map((_, i) => <line key={`v${i}`} x1={`${i * 2.78}%`} y1="0" x2={`${i * 2.78}%`} y2="100%" stroke="#3b82f6" strokeWidth="0.5" />)}
        {/* Equator */}
        <line x1="0" y1="50%" x2="100%" y2="50%" stroke="#3b82f6" strokeWidth="1" opacity="0.3" />
      </svg>

      {/* Ocean current arcs across the map */}
      {type === 'oceans' && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 50" preserveAspectRatio="none">
          <defs>
            {items.map(c => (
              <marker key={`m-${c.id}`} id={`arrow-${c.id}`} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 Z" fill={c.color} />
              </marker>
            ))}
          </defs>
          {items.map(c => {
            const isActive = activeItem === c.id;
            const fx = c.from.x / 2, fy = c.from.y / 2;
            const tx = c.to.x / 2,   ty = c.to.y / 2;
            const mx = (fx + tx) / 2, my = (fy + ty) / 2 - Math.min(4, Math.abs(tx - fx) * 0.3);
            return (
              <path key={`arc-${c.id}`} d={`M ${fx} ${fy} Q ${mx} ${my} ${tx} ${ty}`}
                fill="none" stroke={c.color}
                strokeWidth={isActive ? 0.9 : 0.5}
                strokeDasharray={isActive ? '0' : '1.4 1'}
                opacity={isActive ? 1 : 0.65}
                markerEnd={`url(#arrow-${c.id})`} />
            );
          })}
        </svg>
      )}

      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="landGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.14)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.06)" />
          </linearGradient>
        </defs>
        {COUNTRY_PATHS.map(c => (
          <path key={c.id} d={c.d} fill="url(#landGrad)" stroke="rgba(148,197,255,0.28)" strokeWidth="0.6" strokeLinejoin="round">
            <title>{c.name}</title>
          </path>
        ))}
      </svg>

      {/* Items */}
      {positioned.map(item => {
        const isActive = activeItem === item.id;
        const Icon = item.icon;
        return (
          <motion.button key={item.id}
            onClick={() => onItemClick(item.id)}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
            style={{ left: `${item.x}%`, top: `${item.y}%` }}
            whileHover={{ scale: 1.3 }}
            whileTap={{ scale: 0.9 }}
            data-testid={`map-item-${item.id}`}>
            {type === 'tectonics' ? (
              <div className={`px-2 py-1 rounded-lg text-[9px] font-bold border transition-all ${isActive ? 'bg-muted border-primary text-foreground scale-110' : 'bg-black/40 border-border text-muted-foreground hover:text-foreground'}`}
                style={isActive ? { borderColor: item.color, color: item.color } : {}}>
                {item.name.split(' ')[0]}
              </div>
            ) : type === 'oceans' ? (
              <div className={`px-2 py-1 rounded-full text-[9px] font-bold border-2 transition-all whitespace-nowrap ${isActive ? 'scale-110 shadow-[0_0_15px_2px] text-white' : 'text-white/90 hover:scale-105'}`}
                style={{ borderColor: item.color, backgroundColor: item.color + (isActive ? 'e0' : '99'), boxShadow: isActive ? `0 0 15px 2px ${item.color}` : 'none' }}
                data-testid={`current-marker-${item.id}`}>
                {item.name.split(' ')[0]}
              </div>
            ) : (
              <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                isActive ? 'ring-2 ring-offset-1 ring-offset-[#0a1628]' : 'hover:ring-1 ring-white/30'
              }`} style={{ backgroundColor: item.color + (isActive ? '' : '80'), ringColor: item.color }}>
                {Icon ? <Icon className="w-3 h-3 text-white" /> : <MapPin className="w-3 h-3 text-white" />}
              </div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────
export default function EarthExplorerLab() {
  const navigate = useNavigate();
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission } = useLabProgress('earth-explorer', MISSIONS.length);
  const [showMissions, setShowMissions] = useState(true);
  const [discovered, setDiscovered] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const [score, setScore] = useState(0);
  // Climate change sim
  const [co2Level, setCo2Level] = useState(400);
  // Pangaea slider
  const [timeSlider, setTimeSlider] = useState(250);

  const mission = MISSIONS[currentMission];

  const selectMission = (idx) => {
    if (!unlockedMissions.includes(idx)) return;
    setCurrentMission(idx);
    setShowMissions(false);
    setDiscovered([]);
    setActiveItem(null);
    setCo2Level(400);
    setTimeSlider(250);
  };

  const handleItemClick = useCallback((id) => {
    setActiveItem(prev => prev === id ? null : id);
    if (!discovered.includes(id)) {
      const newDisc = [...discovered, id];
      setDiscovered(newDisc);
      if (newDisc.length >= mission.targetCount && !completedMissions.includes(currentMission)) {
        completeMission(currentMission);
        setScore(prev => prev + mission.difficulty * 15);
      }
    }
  }, [discovered, mission, currentMission, completedMissions, completeMission]);

  const checkClimateComplete = useCallback((lvl) => {
    const thresholds = [400, 600, 800];
    const reached = thresholds.filter(t => Math.abs(lvl - t) < 30);
    const newDisc = [...new Set([...discovered, ...reached.map(t => `co2-${t}`)])];
    setDiscovered(newDisc);
    if (newDisc.length >= 3 && !completedMissions.includes(currentMission)) {
      completeMission(currentMission);
      setScore(prev => prev + mission.difficulty * 15);
    }
  }, [discovered, mission, currentMission, completedMissions, completeMission]);

  const getActiveItemData = () => {
    if (!activeItem) return null;
    switch (mission.type) {
      case 'tectonics': return TECTONIC_PLATES.find(p => p.id === activeItem);
      case 'countries': return COUNTRIES.find(c => c.id === activeItem);
      case 'disasters': return DISASTERS.find(d => d.id === activeItem);
      case 'mountains': return MOUNTAINS.find(m => m.id === activeItem);
      case 'oceans': return OCEAN_CURRENTS.find(o => o.id === activeItem);
      default: return null;
    }
  };

  const isComplete = discovered.length >= mission.targetCount;
  const itemData = getActiveItemData();

  // ── Mission Selector ──
  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/labs')} className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center shadow-lg shadow-sky-500/30"><Globe className="w-5 h-5 text-white" /></div>
                <div><h1 className="text-base font-bold">Earth Explorer 3D</h1><p className="text-[10px] text-muted-foreground">Tectonics, Climate, Countries</p></div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10"><Star className="w-3 h-3 mr-1 fill-amber-400" />{score}</Badge>
              <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 bg-emerald-400/10">{completedMissions.length}/10</Badge>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Explore Our Planet</h2>
            <p className="text-muted-foreground text-sm">Interactive geography — tectonics, climate, countries, and more!</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const unlocked = unlockedMissions.includes(idx);
              const completed = completedMissions.includes(idx);
              return (
                <motion.button key={m.id} onClick={() => selectMission(idx)} disabled={!unlocked}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                  className={`p-5 rounded-2xl border text-left transition-all ${completed ? 'border-emerald-500/40 bg-emerald-500/5' : unlocked ? 'border-border bg-card hover:bg-muted' : 'border-white/5 opacity-70 cursor-not-allowed'}`}
                  data-testid={`mission-${idx}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${completed ? 'bg-emerald-500 text-white' : unlocked ? 'bg-muted' : 'bg-muted/50 text-muted-foreground/60'}`}>
                        {completed ? <Check className="w-4 h-4" /> : unlocked ? idx + 1 : <Lock className="w-3 h-3" />}
                      </span>
                      <h3 className="font-semibold text-sm">{m.title}</h3>
                    </div>
                    <div className="flex gap-0.5">{[...Array(m.difficulty)].map((_,i) => <Star key={i} className={`w-3 h-3 ${completed ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}`} />)}</div>
                  </div>
                  <p className="text-xs text-muted-foreground">{m.description}</p>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  // ── Map-based missions ──
  const renderMapMission = () => {
    let items = [];
    let type = 'default';
    switch (mission.type) {
      case 'tectonics': items = TECTONIC_PLATES; type = 'tectonics'; break;
      case 'countries': items = COUNTRIES; type = 'default'; break;
      case 'disasters': items = DISASTERS; type = 'default'; break;
      case 'mountains': items = MOUNTAINS; type = 'default'; break;
      case 'oceans': items = OCEAN_CURRENTS; type = 'oceans'; break;
      default: break;
    }
    return (
      <>
        <WorldMap items={items} onItemClick={handleItemClick} activeItem={activeItem} type={type} />
        <AnimatePresence>
          {itemData && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="p-4 rounded-2xl border bg-card" style={{ borderColor: (itemData.color || '#fff') + '40' }}
              data-testid="item-info">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: (itemData.color || '#fff') + '20' }}>
                  {itemData.icon ? <itemData.icon className="w-4 h-4" style={{ color: itemData.color }} /> : <MapPin className="w-4 h-4" style={{ color: itemData.color }} />}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-sm" style={{ color: itemData.color }}>{itemData.name}</h4>
                  {itemData.capital && <p className="text-xs text-muted-foreground">Capital: {itemData.capital} | Pop: {itemData.pop}</p>}
                  {itemData.height && <p className="text-xs text-muted-foreground">{itemData.height} | {itemData.range}</p>}
                  {itemData.temp && <p className="text-xs text-muted-foreground">{itemData.temp}</p>}
                  {itemData.magnitude && <p className="text-xs text-muted-foreground">{itemData.location} | {itemData.magnitude}</p>}
                  <p className="text-sm text-foreground/80 mt-1.5">{itemData.info || itemData.fact || itemData.desc}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  };

  // ── Specialized missions ──
  const renderSpecialMission = () => {
    switch (mission.type) {
      case 'boundaries': {
        const boundaries = [
          { id: 'b1', type: 'Convergent', location: 'Himalayas', desc: 'Indian + Eurasian plates collide → mountains form', color: '#ef4444' },
          { id: 'b2', type: 'Divergent', location: 'Mid-Atlantic Ridge', desc: 'Plates pull apart → new crust forms from magma', color: '#3b82f6' },
          { id: 'b3', type: 'Transform', location: 'San Andreas Fault', desc: 'Plates slide past each other → earthquakes', color: '#22c55e' },
          { id: 'b4', type: 'Convergent', location: 'Mariana Trench', desc: 'Pacific plate subducts under Philippine → deepest point on Earth', color: '#ef4444' },
          { id: 'b5', type: 'Divergent', location: 'East African Rift', desc: 'African plate splitting → will form new ocean', color: '#3b82f6' },
          { id: 'b6', type: 'Transform', location: 'Alpine Fault, NZ', desc: 'Pacific + Australian plates slide → earthquakes', color: '#22c55e' },
        ];
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 mb-4">
              {['Convergent', 'Divergent', 'Transform'].map(t => (
                <div key={t} className="p-3 rounded-xl border border-border bg-card text-center">
                  <div className="w-8 h-8 rounded-full mx-auto mb-1" style={{ backgroundColor: t === 'Convergent' ? '#ef4444' : t === 'Divergent' ? '#3b82f6' : '#22c55e', opacity: 0.3 }}>
                    <span className="text-[20px] leading-8">{t === 'Convergent' ? '→←' : t === 'Divergent' ? '←→' : '↑↓'}</span>
                  </div>
                  <p className="text-xs font-bold text-foreground/80">{t}</p>
                </div>
              ))}
            </div>
            {boundaries.map(b => {
              const isDisc = discovered.includes(b.id);
              return (
                <motion.button key={b.id} onClick={() => handleItemClick(b.id)}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${isDisc ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border bg-card hover:bg-muted/50'}`}
                  data-testid={`boundary-${b.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: b.color }} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{b.location} <span className="text-muted-foreground/70 text-xs">({b.type})</span></p>
                      <p className="text-xs text-muted-foreground">{b.desc}</p>
                    </div>
                    {isDisc && <Check className="w-4 h-4 text-emerald-400" />}
                  </div>
                </motion.button>
              );
            })}
          </div>
        );
      }
      case 'climate': {
        return (
          <div className="space-y-3">
            {CLIMATE_ZONES.map(zone => {
              const isDisc = discovered.includes(zone.id);
              return (
                <motion.button key={zone.id} onClick={() => handleItemClick(zone.id)}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${isDisc ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border bg-card hover:bg-muted/50'}`}
                  data-testid={`climate-${zone.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-4 h-4 rounded-full mt-0.5 shrink-0" style={{ backgroundColor: zone.color }} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold" style={{ color: zone.color }}>{zone.name}</p>
                        <span className="text-xs text-muted-foreground/70">{zone.temp}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{zone.desc}</p>
                      <div className="flex gap-1 mt-1.5">{zone.regions.map(r => <span key={r} className="text-[9px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground/70">{r}</span>)}</div>
                    </div>
                    {isDisc && <Check className="w-4 h-4 text-emerald-400 shrink-0" />}
                  </div>
                </motion.button>
              );
            })}
          </div>
        );
      }
      case 'layers': {
        return (
          <div className="space-y-2">
            <div className="relative rounded-2xl overflow-hidden border border-border">
              {EARTH_LAYERS.map((layer, idx) => {
                const isDisc = discovered.includes(layer.id);
                return (
                  <motion.button key={layer.id} onClick={() => handleItemClick(layer.id)}
                    className={`w-full p-4 text-left transition-all border-b border-white/5 last:border-b-0 ${activeItem === layer.id ? 'bg-muted' : 'bg-card hover:bg-white/[0.05]'}`}
                    style={{ borderLeftWidth: 4, borderLeftColor: layer.color }}
                    data-testid={`layer-${layer.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold" style={{ color: layer.color }}>{layer.name}</p>
                        <p className="text-[10px] text-muted-foreground/70">{layer.depth} | {layer.temp}</p>
                      </div>
                      {isDisc && <Check className="w-4 h-4 text-emerald-400" />}
                    </div>
                    {activeItem === layer.id && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-muted-foreground mt-2">{layer.desc}</motion.p>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        );
      }
      case 'climate-change': {
        const tempRise = ((co2Level - 280) / 280 * 3).toFixed(1);
        const seaRise = ((co2Level - 280) / 280 * 60).toFixed(0);
        return (
          <div className="space-y-4">
            <div className="p-5 rounded-2xl border border-border bg-card" data-testid="climate-sim">
              <h3 className="text-sm font-bold mb-4">CO₂ Concentration (ppm)</h3>
              <input type="range" min={280} max={1000} step={10} value={co2Level}
                onChange={e => { const v = parseInt(e.target.value); setCo2Level(v); checkClimateComplete(v); }}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ accentColor: co2Level > 600 ? '#ef4444' : co2Level > 450 ? '#f59e0b' : '#22c55e', background: `linear-gradient(to right, #22c55e, #f59e0b, #ef4444)` }}
                data-testid="co2-slider" />
              <div className="flex justify-between text-[10px] text-muted-foreground/70 mt-1">
                <span>280 (pre-industrial)</span><span>Current ~420</span><span>1000</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 rounded-xl border border-border bg-card text-center">
                <Thermometer className="w-5 h-5 mx-auto mb-1 text-red-400" />
                <p className="text-xl font-bold text-red-400">+{tempRise}°C</p>
                <p className="text-[10px] text-muted-foreground/70">Temp Rise</p>
              </div>
              <div className="p-4 rounded-xl border border-border bg-card text-center">
                <Waves className="w-5 h-5 mx-auto mb-1 text-blue-400" />
                <p className="text-xl font-bold text-blue-400">+{seaRise}cm</p>
                <p className="text-[10px] text-muted-foreground/70">Sea Level</p>
              </div>
              <div className="p-4 rounded-xl border border-border bg-card text-center">
                <Cloud className="w-5 h-5 mx-auto mb-1 text-amber-400" />
                <p className="text-xl font-bold text-amber-400">{co2Level}</p>
                <p className="text-[10px] text-muted-foreground/70">CO₂ ppm</p>
              </div>
            </div>
            <div className={`p-4 rounded-xl border text-sm ${co2Level > 600 ? 'border-red-500/30 bg-red-500/5 text-red-300' : co2Level > 450 ? 'border-amber-500/30 bg-amber-500/5 text-amber-300' : 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300'}`}>
              {co2Level <= 350 && 'Pre-industrial levels. Stable climate, predictable seasons.'}
              {co2Level > 350 && co2Level <= 450 && 'Current levels. Already seeing effects: melting glaciers, stronger storms, coral bleaching.'}
              {co2Level > 450 && co2Level <= 600 && 'Dangerous! Many island nations flood. Mass coral death. Arctic ice-free in summer.'}
              {co2Level > 600 && co2Level <= 800 && 'Catastrophic! Meters of sea level rise. Mass extinction. Many cities underwater.'}
              {co2Level > 800 && 'Extreme! Earth resembles a hothouse. Most of current coastlines submerged. Uninhabitable tropics.'}
            </div>
          </div>
        );
      }
      case 'pangaea': {
        const progress = (250 - timeSlider) / 250;
        return (
          <div className="space-y-4" data-testid="pangaea-sim">
            <div className="relative w-full aspect-[2/1] rounded-2xl overflow-hidden border border-border bg-muted dark:bg-[#0a1628]">
              <svg viewBox="0 0 100 50" className="w-full h-full">
                {/* Simplified Pangaea breakup animation */}
                <ellipse cx={50 - progress * 15} cy={25 + progress * 8} rx={12 - progress * 3} ry={10 - progress * 2} fill="#22c55e" opacity="0.5">
                  <title>Africa</title>
                </ellipse>
                <ellipse cx={50 - progress * 30} cy={25 - progress * 5} rx={10 - progress * 2} ry={8 - progress * 1} fill="#3b82f6" opacity="0.5">
                  <title>Americas</title>
                </ellipse>
                <ellipse cx={50 + progress * 20} cy={25 - progress * 10} rx={15 - progress * 2} ry={8 - progress * 1} fill="#a78bfa" opacity="0.5">
                  <title>Eurasia</title>
                </ellipse>
                <ellipse cx={50 + progress * 25} cy={25 + progress * 15} rx={8 - progress * 1} ry={6 - progress * 0.5} fill="#f59e0b" opacity="0.5">
                  <title>Australia</title>
                </ellipse>
                <ellipse cx={50} cy={25 + progress * 22} rx={15 - progress * 2} ry={5 - progress * 1} fill="#e2e8f0" opacity="0.3">
                  <title>Antarctica</title>
                </ellipse>
              </svg>
              <div className="absolute top-3 left-3 text-xs text-muted-foreground font-mono">{timeSlider} million years ago</div>
            </div>
            <input type="range" min={0} max={250} step={5} value={timeSlider}
              onChange={e => { setTimeSlider(parseInt(e.target.value)); if (parseInt(e.target.value) === 0 && !discovered.includes('pangaea-done')) { handleItemClick('pangaea-done'); }}}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: '#a78bfa', background: 'linear-gradient(to right, #3b82f6, #a78bfa)' }}
              data-testid="time-slider" />
            <div className="flex justify-between text-[10px] text-muted-foreground/70">
              <span>Present Day</span><span>250 Mya (Pangaea)</span>
            </div>
            <div className="p-3 rounded-xl border border-border bg-card text-xs text-muted-foreground">
              {timeSlider > 200 && 'All continents are joined as one supercontinent: Pangaea ("All Earth").'}
              {timeSlider > 100 && timeSlider <= 200 && 'Pangaea begins to rift! Laurasia (north) separates from Gondwana (south).'}
              {timeSlider > 50 && timeSlider <= 100 && 'The Atlantic Ocean widens. India races toward Eurasia. Australia separates from Antarctica.'}
              {timeSlider <= 50 && timeSlider > 0 && 'India collides with Eurasia (Himalayas form). Continents approaching modern positions.'}
              {timeSlider === 0 && 'Present day! But the continents are still moving. In 250 million years, a new supercontinent may form: Pangaea Ultima!'}
            </div>
          </div>
        );
      }
      default: return renderMapMission();
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="earth-explorer">
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setShowMissions(true)} className="text-foreground/80 hover:text-foreground hover:bg-muted" data-testid="back-to-missions"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-sm font-bold">Mission {currentMission + 1}: {mission.title}</h1>
              <p className="text-[10px] text-muted-foreground/70">{mission.goal}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-muted-foreground border-white/20">{discovered.length}/{mission.targetCount} found</Badge>
            <Badge variant="outline" className="text-amber-400 border-amber-400/30 bg-amber-400/10"><Star className="w-3 h-3 mr-1 fill-amber-400" />{score}</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6">
          <div className="space-y-4">
            {['tectonics', 'countries', 'disasters', 'mountains', 'oceans'].includes(mission.type) ? renderMapMission() : renderSpecialMission()}

            {isComplete && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="p-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 text-center" data-testid="mission-complete">
                <p className="text-emerald-400 font-bold text-lg mb-2">Mission Complete!</p>
                <div className="flex gap-3 justify-center">
                  {currentMission < MISSIONS.length - 1 && (
                    <Button onClick={() => selectMission(currentMission + 1)}
                      className="bg-gradient-to-r from-sky-600 to-blue-600" data-testid="next-mission-btn">
                      Next Mission <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  <Button onClick={() => setShowMissions(true)} variant="outline" className="border-white/20 text-foreground/80">All Missions</Button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right panel */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="text-sm font-semibold mb-2">Progress</h3>
              <div className="space-y-1">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => unlockedMissions.includes(idx) && selectMission(idx)}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' : completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' : unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`} data-testid={`sidebar-mission-${idx}`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${completedMissions.includes(idx) ? 'bg-emerald-500 text-white' : idx === currentMission ? 'bg-muted' : 'bg-muted/50'}`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
